import * as THREE from 'three';
import { bronze } from '../hud/hud-materials.js';
import { iron, polygonFrameGeometry } from '../hud/hud-parts.js';

/**
 * The game's pointers, modelled in the 3D HUD's bronze and lit like it, then baked once into small images for CSS cursors and the
 * pointer-lock cursor. Presentation only: which one shows is decided by what the client already knows is under the pointer.
 */
export type CursorName = 'default' | 'press' | 'move' | 'attack' | 'talk' | 'pickup' | 'cast' | 'invalid' | 'grab' | 'aim';

export interface BakedCursor {
  /** `cast` takes a suffix for a school's gem (`cast-fire`); the plain `cast` has an amber gem. */
  name: string;
  /** The image at 1x and 2x (`SIZE` and twice `SIZE` pixels square). */
  images: [HTMLCanvasElement, HTMLCanvasElement];
  /** The pixel, in CSS pixels from the top left, that the click lands on. */
  hotspot: [number, number];
}

/** CSS pixels across one cursor. Browsers drop larger cursors near the window's edge. */
export const CURSOR_SIZE = 32;
const SUPERSAMPLE = 4;

const gilt = () => new THREE.MeshStandardMaterial({ color: 0xc8984c, metalness: .75, roughness: .34 });
const steel = () => new THREE.MeshStandardMaterial({ color: 0x8c8a84, metalness: .8, roughness: .32 });
const leather = () => new THREE.MeshStandardMaterial({ color: 0x3a2617, metalness: 0, roughness: .85 });
const rust = () => new THREE.MeshStandardMaterial({ color: 0x6a2618, metalness: .55, roughness: .5 });

/** A model and the point on it that clicks. Models live in cursor pixels: x right, y up from the bottom, so the top left is (0, 32). */
interface Model { group: THREE.Group; tip: THREE.Vector3 }

const extrude = (shape: THREE.Shape, depth: number, bevel: number) =>
  new THREE.ExtrudeGeometry(shape, { depth, bevelEnabled: bevel > 0, bevelSize: bevel, bevelThickness: bevel, bevelSegments: 2, curveSegments: 16 });
const shape = (points: [number, number][]) => new THREE.Shape(points.map(([x, y]) => new THREE.Vector2(x, y)));

/** The plain pointer: a cast arrowhead, its face the HUD's hammered bronze and its bevelled edge gilt. Tip at the origin. */
function arrow(face: THREE.Material, edge: THREE.Material): THREE.Mesh {
  const outline = shape([[0, 0], [0, -21], [5.2, -16.4], [9, -24.6], [12.4, -23.1], [8.7, -15], [15.2, -15]]);
  return new THREE.Mesh(extrude(outline, 2, 1), [face, edge]);
}

function pointer(face: THREE.Material, edge: THREE.Material): Model {
  const group = new THREE.Group(); group.add(arrow(face, edge));
  group.position.set(3, 29, 0);
  return { group, tip: new THREE.Vector3(0, 0, 0) };
}

/** Ground: four gently curved bronze arrows closing on the spot from the corners. */
function move(): Model {
  const group = new THREE.Group(), face = bronze(7), edge = gilt();
  for (let i = 0; i < 4; i++) {
    const a = Math.PI / 4 + i * Math.PI / 2, at = (r: number, turn: number) => new THREE.Vector3(Math.cos(a + turn) * r, Math.sin(a + turn) * r, 0);
    const curve = new THREE.QuadraticBezierCurve3(at(15, 0), at(12, .24), at(8.6, .24));
    const shaft = new THREE.Mesh(new THREE.TubeGeometry(curve, 16, 1.05, 8), edge); group.add(shaft);
    // The head carries on along the shaft's last heading, its point short of the centre.
    const end = curve.getPoint(1), heading = curve.getTangent(1);
    const head = new THREE.Mesh(extrude(shape([[0, 2.9], [-2.6, -1.4], [2.6, -1.4]]), 1, .5), [face, edge]);
    head.position.copy(end).addScaledVector(heading, 1.2); head.position.z = -.5;
    head.rotation.z = Math.atan2(heading.y, heading.x) - Math.PI / 2; group.add(head);
  }
  const stud = new THREE.Mesh(new THREE.SphereGeometry(1.3, 12, 8), edge); stud.position.z = 1; group.add(stud);
  group.position.set(16, 16, 0);
  return { group, tip: new THREE.Vector3() };
}

/** An enemy: a short sword, point up and to the left. */
function attack(): Model {
  const sword = new THREE.Group();
  const blade = new THREE.Mesh(extrude(shape([[0, 0], [-1.9, -4.2], [-1.9, -17], [1.9, -17], [1.9, -4.2]]), .7, .5), steel());
  blade.position.z = -.35;
  const guard = new THREE.Mesh(new THREE.BoxGeometry(10, 2, 2.2), bronze(9)); guard.position.y = -18;
  const grip = new THREE.Mesh(new THREE.CylinderGeometry(1.1, 1.1, 5.6, 12), leather()); grip.position.y = -21.8;
  const pommel = new THREE.Mesh(new THREE.SphereGeometry(1.9, 16, 12), gilt()); pommel.position.y = -25.4;
  sword.add(blade, guard, grip, pommel); sword.rotation.z = Math.PI / 4; sword.scale.setScalar(1.12);
  const group = new THREE.Group(); group.add(sword); group.position.set(3, 29, 0);
  return { group, tip: new THREE.Vector3() };
}

/** A friendly voice: a bronze speech plaque, its tail the point that clicks. */
function talk(): Model {
  const group = new THREE.Group(), face = bronze(11), edge = gilt();
  const plaque = new THREE.Shape(); plaque.absellipse(15, -11, 10.5, 7.5, 0, Math.PI * 2, false, 0);
  group.add(new THREE.Mesh(extrude(plaque, 1.8, .9), [face, edge]));
  group.add(new THREE.Mesh(extrude(shape([[0, 0], [9.2, -5.8], [5.6, -9.2]]), 1.8, .9), [face, edge]));
  for (const x of [10.5, 15, 19.5]) { const stud = new THREE.Mesh(new THREE.SphereGeometry(1.25, 12, 8), edge); stud.position.set(x, -11, 2.6); group.add(stud); }
  group.position.set(2.5, 29.5, 0);
  return { group, tip: new THREE.Vector3() };
}

/** A gauntlet pointing up and to the left, fingers open to take (`open`) or curled into a fist to drag. */
function gauntlet(open: boolean): Model {
  const hand = new THREE.Group(), plate = bronze(13), edge = gilt();
  const palm = new THREE.Mesh(new THREE.BoxGeometry(9.6, 9, 3.4), plate); palm.position.y = -14; hand.add(palm);
  const cuff = new THREE.Mesh(new THREE.CylinderGeometry(4.6, 5.2, 4, 16), edge); cuff.position.y = -20.2; hand.add(cuff);
  const lengths = [6.2, 7.6, 7.2, 5.6];
  const segment = (length: number, material: THREE.Material, x: number, y: number, z: number, tilt: number) => {
    const piece = new THREE.Mesh(new THREE.CapsuleGeometry(1.15, length, 4, 10), material);
    piece.position.set(x, y, z); piece.rotation.x = tilt; hand.add(piece);
  };
  lengths.forEach((length, i) => {
    const x = -3.45 + i * 2.3, material = i % 2 ? plate : edge;
    if (open) { segment(length - 2.3, material, x, -9.6 + length / 2, 0, 0); return; }
    // Knuckle, middle and last joints: up out of the palm, over toward the viewer, then down across the palm's front.
    const reach = length / 7.6;
    segment(1.6 * reach, material, x, -8.6, .2, 0);
    segment(1.5 * reach, material, x, -7.2, 1.9, Math.PI / 2);
    segment(1.8 * reach, material, x, -8.9 - .4 * (1 - reach), 3.6, 0);
  });
  const thumb = new THREE.Mesh(new THREE.CapsuleGeometry(1.25, open ? 3.6 : 4.4, 4, 10), edge);
  if (open) { thumb.position.set(6.4, -13.2, 0); thumb.rotation.z = -.8; }
  else { thumb.position.set(1.2, -12.2, 4.4); thumb.rotation.z = Math.PI / 2 - .25; }
  hand.add(thumb);
  hand.rotation.z = Math.PI / 6; if (!open) hand.scale.setScalar(1.25);
  const group = new THREE.Group(); group.add(hand);
  // The open hand clicks with its longest fingertip; the fist with its knuckles.
  const tip = (open ? new THREE.Vector3(-1.15, -9.6 + lengths[1] + 1.15, 0) : new THREE.Vector3(0, -8, 3)).multiplyScalar(hand.scale.x).applyEuler(hand.rotation);
  group.position.set(open ? 3.5 : 14, open ? 29 : 20, 0).sub(tip);
  return { group, tip };
}

/** A bronze octagon reticle, like the HUD's frames: with a lit gem at its heart when a power is armed, empty to look. */
function reticle(gem: number | null): Model {
  const group = new THREE.Group(), edge = gilt();
  group.add(new THREE.Mesh(polygonFrameGeometry(8, 10, 7.2, 1.4, .7), [bronze(5), edge]));
  for (let i = 0; i < 4; i++) {
    const prong = new THREE.Mesh(extrude(shape([[-1.5, 0], [1.5, 0], [0, gem === null ? -4.6 : -3.4]]), 1, .4), edge);
    const angle = i * Math.PI / 2; prong.position.set(Math.sin(angle) * 7.6, Math.cos(angle) * 7.6, .3); prong.rotation.z = -angle; group.add(prong);
  }
  if (gem !== null) {
    const stone = new THREE.Mesh(new THREE.OctahedronGeometry(3.4, 0), new THREE.MeshStandardMaterial({ color: gem, emissive: gem, emissiveIntensity: .9, metalness: .1, roughness: .2 }));
    stone.scale.z = .6; stone.position.z = 1.5; group.add(stone);
  }
  group.position.set(16, 16, 0);
  return { group, tip: new THREE.Vector3() };
}

/** Nothing to do there: the arrow dulled to iron, behind a rust-iron ring and bar. */
function invalid(): Model {
  const model = pointer(iron(0x3b3834), iron(0x57524a));
  const sign = new THREE.Group();
  sign.add(new THREE.Mesh(new THREE.TorusGeometry(6, 1.3, 10, 36), rust()));
  const bar = new THREE.Mesh(new THREE.BoxGeometry(12, 2.4, 2.4), rust()); bar.rotation.z = -Math.PI / 4; sign.add(bar);
  sign.position.set(19.5 - model.group.position.x, 10.5 - model.group.position.y, 3.4); model.group.add(sign);
  return model;
}

/** Amber for powers that belong to no school. */
const MARTIAL_GEM = 0xe0a040;

function models(gems: Record<string, number>): [string, () => Model][] {
  return [
    ['default', () => pointer(bronze(7), gilt())],
    ['press', () => pointer(new THREE.MeshStandardMaterial({ color: 0xc8a060, metalness: .7, roughness: .36, emissive: 0x5a3a12, emissiveIntensity: .5 }), new THREE.MeshStandardMaterial({ color: 0xf2d08a, metalness: .7, roughness: .3, emissive: 0x7a5418, emissiveIntensity: .6 }))],
    ['move', move], ['attack', attack], ['talk', talk], ['pickup', () => gauntlet(true)], ['grab', () => gauntlet(false)],
    ['invalid', invalid], ['aim', () => reticle(null)], ['cast', () => reticle(MARTIAL_GEM)],
    ...Object.entries(gems).map(([school, color]): [string, () => Model] => [`cast-${school}`, () => reticle(color)]),
  ];
}

/** Draws `source` at `size`, with a dark rim one cursor pixel wide and a soft shadow down and to the right, so it reads on any ground. */
function finish(source: HTMLCanvasElement, size: number): HTMLCanvasElement {
  const unit = size / CURSOR_SIZE;
  const art = document.createElement('canvas'); art.width = art.height = size;
  const context = art.getContext('2d')!; context.imageSmoothingQuality = 'high'; context.drawImage(source, 0, 0, size, size);
  const rim = document.createElement('canvas'); rim.width = rim.height = size;
  const ink = rim.getContext('2d')!;
  for (let i = 0; i < 12; i++) { const a = i / 12 * Math.PI * 2; ink.drawImage(art, Math.cos(a) * unit, Math.sin(a) * unit); }
  ink.globalCompositeOperation = 'source-in'; ink.fillStyle = '#140d07'; ink.fillRect(0, 0, size, size);
  const out = document.createElement('canvas'); out.width = out.height = size;
  const final = out.getContext('2d')!;
  final.shadowColor = '#000000a0'; final.shadowBlur = 2.5 * unit; final.shadowOffsetX = final.shadowOffsetY = unit;
  final.drawImage(rim, 0, 0); final.shadowColor = 'transparent'; final.drawImage(art, 0, 0);
  return out;
}

/**
 * Models every cursor and bakes it with one short-lived WebGL renderer. `gems` are the schools' colours for their `cast-<school>`
 * reticles. Returns nothing where WebGL is missing; the browser's own cursors then stay.
 */
export function bakeCursors(gems: Record<string, number>): BakedCursor[] {
  const pixels = CURSOR_SIZE * SUPERSAMPLE;
  let renderer: THREE.WebGLRenderer;
  try { renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true, preserveDrawingBuffer: true }); } catch { return []; }
  renderer.setPixelRatio(1); renderer.setSize(pixels, pixels, false); renderer.setClearColor(0x000000, 0);
  const camera = new THREE.OrthographicCamera(0, CURSOR_SIZE, CURSOR_SIZE, 0, 1, 200); camera.position.z = 100;
  const scene = new THREE.Scene();
  // The HUD's light: a warm key raking from the upper left, a cool sky fill and a lamp in front so the faces never go black.
  const key = new THREE.DirectionalLight(0xffe8c8, 3); key.position.set(-18, 52, 40); key.target.position.set(16, 16, 0);
  const lamp = new THREE.PointLight(0xffc890, 380, 0, 1.6); lamp.position.set(10, 26, 30);
  scene.add(key, key.target, lamp, new THREE.HemisphereLight(0x8aa0bf, 0x1a120a, 1.2));
  const baked: BakedCursor[] = [];
  for (const [name, build] of models(gems)) {
    const { group, tip } = build();
    // A slight turn toward the light, so bevels and rims read as metal rather than a flat cut-out.
    const turn = new THREE.Group(); turn.add(group); scene.add(turn);
    const pivot = group.localToWorld(tip.clone());
    turn.position.copy(pivot); group.position.sub(pivot); turn.rotation.set(-.28, .32, 0);
    turn.updateMatrixWorld(true);
    renderer.render(scene, camera);
    const at = group.localToWorld(tip.clone());
    const hotspot: [number, number] = [Math.max(0, Math.min(CURSOR_SIZE - 1, Math.round(at.x))), Math.max(0, Math.min(CURSOR_SIZE - 1, Math.round(CURSOR_SIZE - at.y)))];
    baked.push({ name, images: [finish(renderer.domElement, CURSOR_SIZE), finish(renderer.domElement, CURSOR_SIZE * 2)], hotspot });
    scene.remove(turn);
    turn.traverse(object => {
      if (!(object instanceof THREE.Mesh)) return;
      object.geometry.dispose();
      for (const material of [object.material].flat() as THREE.MeshStandardMaterial[]) { material.normalMap?.dispose(); material.dispose(); }
    });
  }
  renderer.dispose(); renderer.forceContextLoss();
  return baked;
}
